import { useEffect, useRef, useState } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Checkbox, Modal, Pagination, Tag, TextInput } from '@carbon/react';
import { Renew } from '@carbon/icons-react';
import type { ColumnDef } from '@tanstack/react-table';
import { Link } from 'react-router';
import { api, ApiError } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { DataTable } from '../components/DataTable';
import { InlineError, PageHeader } from '../components/states';
import { Money, When } from '../components/format';
import { BANK_TX_SOURCE, QueueStatus } from '../components/labels';
import { FilterBar, useUrlFilters, type FilterField } from '../components/Filters';
import { RiskTags } from './Player360';
import { CopyBtn, Reveal } from '../components/Reveal';
import { KiemTraNhanh, NguoiChoiLink } from '../components/KiemTraNhanh';
import { earlierHandler, renewEveryMs, RISK_KIND_LABEL, riskFromRefusal, risksFromRefusal, ticksDone, type Claim, type RiskHold, type RiskItem, type WithdrawRisk } from './walletRisk';

type Req = {
  id: string; nguon: 'nap_rut' | 'cu'; direction: string; status: string; amount: string; createdAt: string; waitingSince: string | null;
  note?: string | null; gameUserId: number; username?: string | null; nickname?: string | null;
  risk?: { marks: { watchlist: boolean; suspectedBot: boolean; suspectedMulti: boolean } | null; withdrawFrozen: boolean; locked: boolean; holds?: RiskHold[] };
  kenh?: string | null; nganHang?: string | null; soTk?: string | null; tenTk?: string | null;
  noiDung?: string | null; nguoiGui?: string | null; mang?: string | null; diaChi?: string | null;
  gia?: string | null; usdtSo?: string | null; trangThai?: string;
  tenDangKy?: string | null; tenLech?: boolean; lyDo?: string | null; maChuyen?: string | null; xuLyLuc?: string | null;
  bankTx?: { id: number; maGd: string; nguon: string; trangThai: string } | null;
  ruiRo?: WithdrawRisk | null;
  // rút 1 bước: the admin handling an open withdrawal while the claim lasts, who paid / rejected it
  claim?: { by: string; until: string } | null; xuLyBoi?: string | null;
};

const gioPhut = (iso: string | null | undefined) => (iso ? new Date(iso).toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit' }) : '');

function RiskScore({ r }: { r?: WithdrawRisk | null }) {
  if (!r) return <span className="muted">—</span>;
  return <span className="tags">
    <Tag size="sm" type={r.canXacNhan ? 'red' : r.diem > 0 ? 'warm-gray' : 'green'} title={r.lyDo.map((x) => x.chu).join('; ') || 'Không có dấu hiệu'}>{r.diem} điểm</Tag>
    {r.lanDau ? <Tag size="sm" type="purple">Lần rút đầu</Tag> : null}
  </span>;
}
type Sla = { nap: number; rut: number } | null;
// bankRefRequired: the wallet config makes a manual bank deposit approval carry the bank reference (off by default)
type Page = { items: Req[]; total: number; page: number; pageSize: number; sla: Sla; bankRefRequired?: boolean; open?: { id: string; status: string; trangThai: string; amount?: string } };

// minutes a pending row has waited, ticking once a minute
function useNow() {
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 30_000); return () => clearInterval(t); }, []);
  return now;
}

function Waiting({ r, sla, now }: { r: Req; sla: Sla; now: number }) {
  if (!r.waitingSince) return <span className="muted">—</span>;
  const min = Math.max(0, Math.floor((now - Date.parse(r.waitingSince)) / 60_000));
  const limit = sla ? (r.direction === 'DEPOSIT' ? sla.nap : sla.rut) : 0;
  const text = min < 60 ? `${min} phút` : `${Math.floor(min / 60)} giờ ${min % 60} phút`;
  return limit > 0 && min >= limit ? <Tag size="sm" type="red" title={`Quá ${limit} phút`}>{text}</Tag> : <span>{text}</span>;
}

const columns = (sla: Sla, now: number): ColumnDef<Req, any>[] => [
  { header: 'Chiều', id: 'chieu', accessorKey: 'direction', cell: (c) => c.getValue() === 'DEPOSIT' ? 'Nạp' : 'Rút' },
  { header: 'Người chơi', id: 'u', cell: (c) => {
    const r = c.row.original;
    return <span className="tags"><NguoiChoiLink id={r.gameUserId} ten={r.username ?? r.nickname} />
      {r.risk?.locked ? <Tag size="sm" type="red">Đã khóa</Tag> : null}
      {r.risk?.withdrawFrozen ? <Tag size="sm" type="red">Dừng rút</Tag> : null}
      <RiskTags marks={r.risk?.marks} /></span>;
  } },
  { header: 'Số tiền', accessorKey: 'amount', cell: (c) => <Money v={c.getValue()} /> },
  { header: 'Kênh', id: 'nh', cell: (c) => c.row.original.nguon === 'cu' ? 'Yêu cầu cũ'
    : c.row.original.kenh === 'usdt' ? 'USDT ' + (c.row.original.mang ?? '') : (c.row.original.nganHang ?? '—') },
  { header: 'Mã', id: 'ma', cell: (c) => c.row.original.noiDung ?? c.row.original.maChuyen ?? '—' },
  { header: 'Người gửi', id: 'ng', cell: (c) => c.row.original.direction === 'DEPOSIT'
    ? <>{c.row.original.nguoiGui ?? '—'}{c.row.original.tenLech ? <Tag type="red" size="sm">khác tên đăng ký</Tag> : null}</>
    : (c.row.original.tenTk ?? '—') },
  { header: 'Rủi ro', id: 'rr', cell: (c) => c.row.original.direction === 'WITHDRAW' ? <RiskScore r={c.row.original.ruiRo} /> : null },
  { header: 'Trạng thái', accessorKey: 'status', cell: (c) => <QueueStatus s={c.getValue()} /> },
  // rút 1 bước: who handles an open withdrawal (claim live), who paid / rejected a closed one
  { header: 'Đang xử lý', id: 'xl', cell: (c) => {
    const r = c.row.original;
    if (r.claim) return <Tag size="sm" type="blue" title={'đến ' + gioPhut(r.claim.until)}>{r.claim.by}</Tag>;
    return r.xuLyBoi ? <span className="muted">{r.xuLyBoi}</span> : <span className="muted">—</span>;
  } },
  { header: 'Đã chờ', id: 'sla', cell: (c) => <Waiting r={c.row.original} sla={sla} now={now} /> },
  { header: 'Lúc', accessorKey: 'createdAt', cell: (c) => <When v={c.getValue()} /> },
];

const FIELDS: FilterField[] = [
  { key: 'status', label: 'Trạng thái', kind: 'select', options: [
    ['', 'Tất cả'], ['PENDING_REVIEW', 'Chờ duyệt'], ['APPROVED', 'Đang chi (rút)'], ['PAID', 'Đã cộng / đã chi'], ['REJECTED', 'Từ chối'],
    ['CANCELLED', 'Người chơi hủy'], ['EXPIRED', 'Hết hạn']] },
  { key: 'player', label: 'Người chơi (ID, tên, nick)', kind: 'text' },
  { key: 'ref', label: 'Mã lệnh / mã chuyển / mã giao dịch', kind: 'text' },
  { key: 'amountMin', label: 'Số tiền từ', kind: 'number' },
  { key: 'amountMax', label: 'Số tiền đến', kind: 'number' },
  { key: 'from', label: 'Từ ngày', kind: 'date' },
  { key: 'to', label: 'Đến ngày', kind: 'date' },
  { key: 'nguon', label: 'Nguồn', kind: 'select', options: [['', 'Sảnh và yêu cầu cũ'], ['game', 'Sảnh (ngân hàng / USDT)'], ['cu', 'Yêu cầu cũ (API người chơi)']] },
];
const KEYS = FIELDS.map((x) => x.key);

// Nap tien va Rut tien la hai trang rieng (owner 2026-10-02, nhu Tele88): chieu co dinh theo duong dan, ghi sau
// bo loc nen ?direction= tren URL khong doi duoc danh sach
// cms-so-du-evol: the house's partner balance at Miracle, which Evolution and the other Miracle live halls draw on (a bet is
// refused at 0). Read on demand; the API reads the provider at most once per cacheSec, so Làm mới inside it shows the same read.
type EvolBalance = { state: 'ok' | 'off' | 'error'; balance?: number; currency?: string; error?: string; readAt: string; cacheSec: number };
const EVOL_ERROR: Record<string, string> = {
  KHONG_KET_NOI: 'không kết nối được Miracle', DU_LIEU_SAI: 'Miracle trả về dữ liệu không đọc được',
  HE_A_KHONG_TRA_LOI: 'hệ A không trả lời', MIRACLE_LOI: 'Miracle báo lỗi',
};
function EvolBalanceCard() {
  const q = useQuery({
    queryKey: ['evol-balance'], queryFn: () => api.get<EvolBalance>('/api/admin/wallet/evol-balance'),
    refetchOnWindowFocus: false, retry: false,
  });
  const d = q.data;
  return (
    <section className="evol" aria-label="Số dư Evolution">
      <div className="evol__main">
        <span className="kpi__label">Số dư Evolution (Miracle)</span>
        {q.isPending ? <span className="evol__value muted">Đang đọc...</span>
          : q.isError ? <span className="evol__error">Không đọc được số dư: {q.error instanceof ApiError ? q.error.message : String(q.error)}</span>
          : d?.state === 'ok' ? <span className="evol__value">{d.currency && d.currency !== 'KRW' ? <>{d.balance?.toLocaleString('vi-VN')} {d.currency}</> : <Money v={d.balance} unit />}</span>
          : d?.state === 'off' ? <span className="evol__error">Chưa cấu hình khóa Miracle trên hệ A</span>
          : <span className="evol__error">Không đọc được số dư: {EVOL_ERROR[d?.error ?? ''] ?? 'Miracle báo lỗi'} ({d?.error})</span>}
        <span className="kpi__hint">
          {d ? <>Đọc lúc <When v={d.readAt} />. </> : null}Evolution và các sảnh live Miracle dùng chung số dư này.
        </span>
      </div>
      <Button kind="tertiary" size="sm" renderIcon={Renew} disabled={q.isFetching} onClick={() => q.refetch()}>
        {q.isFetching && !q.isPending ? 'Đang đọc...' : 'Làm mới'}
      </Button>
    </section>
  );
}

type Chieu = 'DEPOSIT' | 'WITHDRAW';
const LEAD: Record<Chieu, string> = {
  DEPOSIT: 'Lệnh nạp ngân hàng và USDT từ sảnh cùng yêu cầu nạp cũ. Lệnh khớp mã và số tiền với giao dịch ngân hàng (webhook hoặc sao kê) được cộng tự động; giao dịch lệch vào hàng chưa khớp. Duyệt nạp tay cộng tiền một lần. Cột Đã chờ đỏ khi quá thời hạn xử lý.',
  WITHDRAW: 'Lệnh rút ngân hàng và USDT từ sảnh cùng yêu cầu rút cũ. Rút đã trừ ví lúc gửi. Mở lệnh là nhận xử lý (người khác thấy tên bạn và không chi / từ chối được), chép thông tin, chuyển khoản rồi bấm Đã chuyển xong; mã giao dịch không bắt buộc. Cột Đã chờ đỏ khi quá thời hạn xử lý.',
};

export function WalletPage({ chieu }: { chieu: Chieu }) {
  const qc = useQueryClient();
  const { can, me } = useAuth();
  const now = useNow();
  const f = useUrlFilters(KEYS, { defaults: { status: 'PENDING_REVIEW' } });
  const { page, pageSize } = f;
  // the open order's state comes back with each refresh (read on he A), so a decision by another reviewer shows in the dialog
  const moRef = useRef('');
  // sep 2026-10-04 "duyet nap do": the CMS reads a replica 9-20 s behind; the API takes the page's states from he A and the
  // list refreshes every 2 s, so every reviewer sees an approval within about 2 s
  const q = useQuery({
    queryKey: ['wallet', chieu, f.values, page, pageSize],
    queryFn: () => api.get<Page>('/api/admin/wallet', { ...f.query, page, pageSize, direction: chieu, ...(moRef.current ? { open: moRef.current } : {}) }),
    placeholderData: keepPreviousData,
    refetchInterval: 2_000,
  });
  const [pick, setPick] = useState<Req | null>(null);
  moRef.current = pick?.nguon === 'nap_rut' ? pick.id : '';
  const [soNhan, setSoNhan] = useState('');
  const [usdtNhan, setUsdtNhan] = useState('');
  const [lyDo, setLyDo] = useState('');
  const [maCk, setMaCk] = useState('');
  // owner 2026-10-04 (rút 1 bước): one tick on a pending withdrawal's risk list, "Tôi đã kiểm tra kỹ ... chịu hoàn toàn trách nhiệm"
  const [daNhan, setDaNhan] = useState(false);
  // he A asked for the bank reference of this approval (the wallet config requires it since the list loaded)
  const [canMaSau, setCanMaSau] = useState(false);
  // he A found a credited order whose money may be this deposit (TRUNG_KHOAN: a transfer carrying this order's content code
  // went to another order, the same player's same-amount order was credited within the wallet window, or another order
  // on the same receiving account was credited with the same amount or since this order was created): a code-less
  // approval then needs the tick "checked the statement, a separate transfer" (founder 2026-10-03: no code required)
  const [nghiTrung, setNghiTrung] = useState(false);
  const [daKiemTrung, setDaKiemTrung] = useState(false);
  const [goGiu, setGoGiu] = useState('');
  const [giuDoi, setGiuDoi] = useState(false);
  // client 2026-10-02: Duyet / Tu choi / Hoan tien hoi lai (OK / Quay lai) truoc khi gui, do bam nham; doi lenh thi bo
  const [hoi, setHoi] = useState<null | 'duyet' | 'tu-choi' | 'hoan' | 'chi' | 'tiep-quan'>(null);
  useEffect(() => setHoi(null), [pick?.id]);
  // bam dup nut Duyet: cu bam thu hai co the roi trung nut OK vua hien (hop nho nam giua hop lenh), nen OK bo qua
  // moi cu bam trong nua giay dau
  const hoiLuc = useRef(0);
  const hoiLai = (h: 'duyet' | 'tu-choi' | 'hoan' | 'chi' | 'tiep-quan') => { hoiLuc.current = Date.now(); setHoi(h); };
  // rút 1 bước (owner 2026-10-04): opening a lobby withdrawal claims it; `canhBaoTruoc` = the admin who handled it before
  // (taken over, or their claim ran out): they may have transferred already, the reviewer checks the bank statement first
  const [claim, setClaim] = useState<Claim | null>(null);
  const [claimErr, setClaimErr] = useState<unknown>(null);
  const [canhBaoTruoc, setCanhBaoTruoc] = useState<string | null>(null);
  // /pay found a risk that appeared after the list was read: the fresh list is on screen, tick again
  const [ruiRoMoi, setRuiRoMoi] = useState(false);
  // the open row's freeze and holds follow the list (a reload after a refused release, the 30 s refresh). A
  // changed set of holds clears the note: the release sends the ids on screen, so the reviewer checks the new
  // ones and writes the note again before it can be sent
  useEffect(() => {
    const o = q.data?.open;
    if (pick && o && o.id === pick.id && (o.status !== pick.status || o.trangThai !== pick.trangThai || (o.amount && o.amount !== pick.amount))) {
      setPick((p) => (p && p.id === o.id ? { ...p, status: o.status, trangThai: o.trangThai, ...(o.amount ? { amount: o.amount } : {}) } : p));
    }
    const n = pick && q.data?.items.find((x) => x.id === pick.id);
    if (!pick || !n?.risk) return;
    const ids = (h?: RiskHold[]) => (h ?? []).map((x) => x.id).sort().join(',');
    if (ids(n.risk.holds) !== ids(pick.risk?.holds) && (n.risk.holds?.length ?? 0) > 0) { setGoGiu(''); setGiuDoi(true); }
    setPick((p) => (p && p.id === n.id ? { ...p, risk: n.risk } : p));
  }, [q.data]); // eslint-disable-line react-hooks/exhaustive-deps
  const dong = () => { setPick(null); setSoNhan(''); setUsdtNhan(''); setLyDo(''); setMaCk(''); setDaNhan(false); setCanMaSau(false); setNghiTrung(false); setDaKiemTrung(false); setGoGiu(''); setGiuDoi(false); setClaim(null); setClaimErr(null); setCanhBaoTruoc(null); setRuiRoMoi(false); };
  const decide = useMutation({
    mutationFn: (approve: boolean) => api.post('/api/admin/wallet/' + pick!.id + '/decide', {
      approve, amount: pick?.amount === '0' && pick.kenh !== 'usdt' && soNhan ? Number(soNhan) : undefined,
      usdtAmount: pick?.amount === '0' && pick.kenh === 'usdt' && usdtNhan ? usdtNhan : undefined,
      note: lyDo.trim() || undefined, bankRef: approve && maCk.trim() ? maCk.trim() : undefined,
      sameMoneyChecked: approve && !maCk.trim() && nghiTrung && daKiemTrung ? true : undefined,
      ...(approve && items.length ? { riskAck: daNhan, riskSeen: items.map((i) => i.code) } : {}),
    }),
    onSuccess: () => { dong(); qc.invalidateQueries({ queryKey: ['wallet'] }); },
    // he A recomputed the risk at hold time and it now needs the confirmation (order from before P1-D19,
    // lowered threshold): show it with the checkbox so the next approve carries riskAck
    onError: (e) => {
      setHoi(null); // loi hien trong hop lenh, khong de hop xac nhan nao mo lai
      // another reviewer already handled it: reload so the list and the dialog show its state
      if (e instanceof ApiError && e.code === 'STATE') { qc.invalidateQueries({ queryKey: ['wallet'] }); return; }
      if (e instanceof ApiError && e.code === 'MA_GD') { setCanMaSau(true); return; }
      if (e instanceof ApiError && e.code === 'TRUNG_KHOAN') { setNghiTrung(true); setDaKiemTrung(false); return; }
      // a risk the list did not have when it loaded (a lock, a freeze, a bet, a hold): the API sends the list as it is now,
      // the ticks start again so the reviewer reads the new risk before approving
      const fresh = risksFromRefusal(e);
      if (fresh) {
        qc.setQueryData(['wallet-risks', pick?.id], { items: fresh });
        setDaNhan(false);
        const rr = riskFromRefusal(e);
        if (rr) setPick((p) => (p ? { ...p, ruiRo: rr } : p));
        qc.invalidateQueries({ queryKey: ['wallet'] });
      }
    },
  });
  const pay = useMutation({
    // the bank reference is optional (founder 2026-10-04): an empty field sends none. A pending lobby withdrawal is paid in one
    // step (rút 1 bước) with the one risk tick and the codes on screen
    mutationFn: () => api.post('/api/admin/wallet/' + pick!.id + '/pay', {
      ...(maCk.trim() ? { bankRef: maCk.trim() } : {}),
      ...(items.length ? { riskAck: daNhan, riskSeen: items.map((i) => i.code) } : {}),
    }),
    onSuccess: () => { dong(); qc.invalidateQueries({ queryKey: ['wallet'] }); },
    onError: (e) => {
      setHoi(null);
      // a risk appeared after the list was read: the fresh list replaces it, the tick starts again; the order stays claimed
      const fresh = risksFromRefusal(e);
      if (fresh) {
        qc.setQueryData(['wallet-risks', pick?.id], { items: fresh });
        setDaNhan(false);
        setRuiRoMoi(true);
        const rr = riskFromRefusal(e);
        if (rr) setPick((p) => (p ? { ...p, ruiRo: rr } : p));
      }
      if (e instanceof ApiError && e.code === 'DANG_XU_LY') void nhanLenh(false);
      qc.invalidateQueries({ queryKey: ['wallet'] });
    },
  });
  // P1-D20: the reviewer checked the risk events that froze this player's withdrawals and lifts the freeze
  const release = useMutation({
    mutationFn: () => api.post('/api/admin/wallet/' + pick!.id + '/release-hold', { note: goGiu.trim(), ids: (pick!.risk?.holds ?? []).map((h) => h.id) }),
    onSuccess: () => {
      setPick((p) => (p?.risk ? { ...p, risk: { ...p.risk, withdrawFrozen: false, holds: [] } } : p));
      setGoGiu('');
      setGiuDoi(false);
      decide.reset();
      qc.invalidateQueries({ queryKey: ['wallet'] });
      qc.invalidateQueries({ queryKey: ['wallet-risks'] });
    },
    // the holds changed since the list loaded: reload so the reviewer sees and checks the new ones
    onError: () => qc.invalidateQueries({ queryKey: ['wallet'] }),
  });
  const resetMk = useMutation({
    mutationFn: () => api.post('/api/admin/wallet/withdraw-password/reset', { gameUserId: pick!.gameUserId, note: lyDo.trim() || 'Người chơi quên mật khẩu rút' }),
    onSuccess: () => dong(),
  });
  const game = pick?.nguon === 'nap_rut';
  const hetHan = pick?.status === 'EXPIRED' && pick.direction === 'DEPOSIT';
  // rút 1 bước: an open lobby withdrawal is claimed by the admin who may pay it
  const rutMo = game && pick?.direction === 'WITHDRAW' && (pick.status === 'PENDING_REVIEW' || pick.status === 'APPROVED') && can('wallet.pay');
  // the open order now: a claim answer that arrives after the dialog moved to another order is dropped
  const pickId = useRef<string | null>(null);
  pickId.current = pick?.id ?? null;
  async function nhanLenh(takeover: boolean) {
    if (!pick) return;
    const id = pick.id;
    try {
      const r = await api.post<{ claim: Claim }>('/api/admin/wallet/' + id + '/claim', takeover ? { takeover: true } : {});
      if (pickId.current !== id) return;
      setClaim(r.claim);
      setClaimErr(null);
      const truoc = earlierHandler(r.claim);
      if (truoc) setCanhBaoTruoc(truoc);
      // the list's handler column follows a new holder at once (not at the next 30 s refresh)
      if (r.claim.by !== (pick.claim?.by ?? null)) qc.invalidateQueries({ queryKey: ['wallet'] });
    } catch (e) {
      if (pickId.current === id) setClaimErr(e);
    }
  }
  useEffect(() => {
    if (!rutMo) return;
    void nhanLenh(false);
  }, [pick?.id, rutMo]); // eslint-disable-line react-hooks/exhaustive-deps
  // renew while the dialog is open (closing it does not release: an accidental close after a transfer still blocks others)
  useEffect(() => {
    if (!rutMo || !claim?.mine) return;
    const t = setInterval(() => { void nhanLenh(false); }, renewEveryMs(claim.ttlMin));
    return () => clearInterval(t);
  }, [pick?.id, rutMo, claim?.mine, claim?.ttlMin]); // eslint-disable-line react-hooks/exhaustive-deps
  const nhaLenh = useMutation({
    mutationFn: () => api.post('/api/admin/wallet/' + pick!.id + '/release', {}),
    onSuccess: () => { dong(); qc.invalidateQueries({ queryKey: ['wallet'] }); },
  });
  const cuaToi = !rutMo || !!claim?.mine;
  const laChu = me?.role === 'founder';
  // what this admin may do on the picked row: each button needs its own permission
  const choDuyet = (pick?.status === 'PENDING_REVIEW' || hetHan) && can('wallet.review');
  const choTra = pick?.status === 'APPROVED' && pick.direction === 'WITHDRAW' && can('wallet.pay');
  const hoanRut = pick?.status === 'APPROVED' && pick.direction === 'WITHDRAW' && can('wallet.review');
  // one-tap deposit approval (client 2026-10-02): the bank reference is optional (founder 2026-10-03), shown on every
  // pending bank deposit so a typed one is checked for reuse; required only while the wallet config requires it (or he
  // A asked for it because the config was turned on after the list loaded); he A checks again
  const maNap = game && choDuyet && pick?.direction === 'DEPOSIT' && pick.kenh === 'bank';
  const canMaNap = maNap && (!!q.data?.bankRefRequired || canMaSau);
  const soTienThieu = pick?.amount === '0' && choDuyet && !(pick.kenh === 'usdt' ? usdtNhan : soNhan);
  // owner 2026-10-03: a withdrawal is never refused for risk; the list (lock, freeze, risk holds, bets, wagering, blocklist, rules,
  // first-withdrawal score) must be ticked, both ticks, before Duyệt is enabled. he A not answering = the list errors, Duyệt stays off
  const coDanhSach = choDuyet && pick?.direction === 'WITHDRAW';
  const risksQ = useQuery({
    queryKey: ['wallet-risks', pick?.id],
    queryFn: () => api.get<{ items: RiskItem[] }>('/api/admin/wallet/' + pick!.id + '/risks'),
    enabled: !!pick && coDanhSach, staleTime: 0, gcTime: 0, refetchOnWindowFocus: false,
  });
  const items: RiskItem[] = coDanhSach ? risksQ.data?.items ?? [] : [];
  const maKey = items.map((i) => i.code).join('|');
  // a changed list (a release, a refresh) starts the ticks again
  useEffect(() => { setDaNhan(false); }, [pick?.id, maKey]);
  const doiDanhSach = coDanhSach && (risksQ.isPending || risksQ.isError);
  const chuaTick = coDanhSach && !ticksDone(items, daNhan);
  // the player page is where a lock or a manual freeze is lifted (optional, no longer needed to approve)
  const coKhoaHoacDung = items.some((i) => i.kind === 'lock' || i.kind === 'freeze');
  const coGiuRut = game && coDanhSach && !!pick?.risk?.holds?.length;
  // rút 1 bước: a pending lobby withdrawal needs both permissions (the old approve + the payment); an old held one the payment
  const motBuoc = rutMo && (pick?.status === 'APPROVED' || can('wallet.review'));
  const primary = motBuoc ? 'Đã chuyển xong' : choTra ? 'Xác nhận đã chuyển' : choDuyet ? (pick?.direction === 'WITHDRAW' ? 'Duyệt (giữ lệnh)' : hetHan ? 'Duyệt tay, cộng tiền' : 'Duyệt, cộng tiền') : 'Đóng';
  const khoaChinh = (choDuyet || choTra || motBuoc) && (soTienThieu || decide.isPending || pay.isPending || !cuaToi
    || (canMaNap && !maCk.trim()) || (maNap && nghiTrung && !daKiemTrung && !maCk.trim()) || (hetHan && !lyDo.trim()) || (choDuyet && (doiDanhSach || chuaTick)));
  // the transfer details are copied only once the risks are ticked (read before sending money) and the order is this admin's
  const khoaChep = !!rutMo && (!cuaToi || (coDanhSach && (doiDanhSach || chuaTick)));
  // another admin's claim: this admin neither pays nor rejects / refunds the order, only closes the dialog
  const secondary = !cuaToi ? 'Đóng' : choDuyet && !hetHan ? 'Từ chối' : hoanRut ? 'Hoàn tiền' : choDuyet || choTra || motBuoc ? 'Đóng' : undefined;
  return (
    <div className="page">
      <PageHeader title={chieu === 'DEPOSIT' ? 'Nạp tiền' : 'Rút tiền'} lead={LEAD[chieu]} />
      {chieu === 'DEPOSIT' ? <EvolBalanceCard /> : null}
      <FilterBar id="w" fields={FIELDS} filters={f} exportPath="/api/admin/wallet" exportQuery={{ direction: chieu }}>
        <Link to="/wallet/bank-tx" className="cds--btn cds--btn--ghost cds--btn--md">Giao dịch ngân hàng</Link>
      </FilterBar>
      <DataTable columns={columns(q.data?.sla ?? null, now).filter((c) => c.id !== 'chieu' && (c.id !== 'rr' || chieu === 'WITHDRAW'))} data={q.data?.items} loading={q.isLoading || q.isPlaceholderData} error={q.error}
        onRetry={() => q.refetch()} onRowClick={(r) => setPick(r)} rowKey={(r) => r.id}
        empty={{ title: 'Không có lệnh', body: `Lệnh ${chieu === 'DEPOSIT' ? 'nạp' : 'rút'} người chơi tạo trong sảnh hiện ở đây.` }} />
      {q.data && q.data.total > 0 ? (
        <Pagination page={page} pageSize={pageSize} pageSizes={[20, 50, 100]} totalItems={q.data.total}
          onChange={({ page: p, pageSize: s }) => f.setPage(p, s)} />
      ) : null}
      <Modal open={!!pick && !hoi} modalHeading={pick ? (pick.direction === 'DEPOSIT' ? 'Nạp' : 'Rút') + ' #' + pick.gameUserId + ' · ' + (pick.username ?? pick.nickname ?? '') : ''}
        primaryButtonText={primary} secondaryButtonText={secondary}
        primaryButtonDisabled={khoaChinh}
        onRequestClose={dong}
        onRequestSubmit={() => { if (motBuoc) hoiLai('chi'); else if (choTra) pay.mutate(); else if (choDuyet) hoiLai('duyet'); else dong(); }}
        onSecondarySubmit={() => { if (decide.isPending) return; if ((choDuyet && !hetHan) || hoanRut) { if (!cuaToi) return; hoiLai(choDuyet && !hetHan ? 'tu-choi' : 'hoan'); } else dong(); }}>
        {pick ? <p>Người chơi <NguoiChoiLink id={pick.gameUserId} ten={pick.username ?? pick.nickname} /></p> : null}
        <p>Số tiền <Money v={pick?.amount} /> · <QueueStatus s={pick?.status ?? ''} /></p>
        {pick?.waitingSince ? <p>Đã chờ: <Waiting r={pick} sla={q.data?.sla ?? null} now={now} /></p> : null}
        {rutMo ? (
          <div className="claim-block" role="status">
            {claim?.mine ? <p><Tag size="sm" type="green">Bạn đang xử lý</Tag> giữ lệnh đến {gioPhut(claim.until)} (tự gia hạn khi hộp này còn mở)</p>
              : claim ? <p><Tag size="sm" type="red">Đang được {claim.by} xử lý</Tag> đến {gioPhut(claim.until)}: bạn không chuyển, không từ chối lệnh này.</p>
                : !claimErr ? <p className="muted">Đang nhận lệnh…</p> : null}
            {canhBaoTruoc ? <p className="num--neg"><strong>{canhBaoTruoc}</strong> đã mở lệnh này trước, có thể đã chuyển khoản: kiểm tra sao kê ngân hàng trước khi chuyển hoặc từ chối.</p> : null}
            {claim && !claim.mine ? (
              <>
                <Button kind="ghost" size="sm" onClick={() => { void nhanLenh(false); }}>Thử nhận lại</Button>
                {laChu ? <Button kind="danger--tertiary" size="sm" onClick={() => hoiLai('tiep-quan')}>Tiếp quản</Button> : null}
              </>
            ) : null}
            {claim?.mine ? <Button kind="ghost" size="sm" disabled={nhaLenh.isPending} onClick={() => nhaLenh.mutate()}>Nhả lệnh</Button> : null}
            <InlineError error={claimErr || nhaLenh.error} />
          </div>
        ) : null}
        {coDanhSach ? (
          risksQ.isError ? null
            : risksQ.isPending ? <p className="muted" role="status">Đang kiểm tra rủi ro của lệnh rút…</p>
              : items.length ? (
                <div className="risk-block" role="group" aria-label="Rủi ro của lệnh rút">
                  {ruiRoMoi ? <p className="num--neg" role="status"><strong>Có rủi ro mới sau khi mở lệnh</strong>: đọc lại danh sách và tích lại.</p> : null}
                  <p><strong>Rủi ro của lệnh rút ({items.length})</strong>: không chặn rút. Đọc trước khi chuyển tiền.</p>
                  <ul>
                    {items.map((i) => <li key={i.code}>
                      <Tag size="sm" type={i.kind === 'lock' || i.kind === 'freeze' ? 'red' : 'warm-gray'}>{RISK_KIND_LABEL[i.kind]}</Tag> {i.label}
                    </li>)}
                  </ul>
                  {coKhoaHoacDung ? <p className="muted">Mở khóa hoặc mở rút (không bắt buộc) ở <Link to={'/users/' + pick!.gameUserId}>trang người chơi</Link>.</p> : null}
                  <Checkbox id="w-risk-ack" checked={daNhan} disabled={!cuaToi} onChange={(_e, { checked }) => setDaNhan(checked)}
                    labelText="Tôi đã kiểm tra kỹ các rủi ro trên. Nếu lệnh rút này có sai sót, tôi chịu hoàn toàn trách nhiệm." />
                </div>
              ) : null
        ) : null}
        {/* owner 2026-10-06: soi nhanh nguoi choi ngay trong cua so duyet rut, chi doc */}
        {pick?.direction === 'WITHDRAW' ? <KiemTraNhanh key={pick.id} lenhId={pick.id} userId={pick.gameUserId} /> : null}
        {pick?.kenh === 'usdt' ? (
          <>
            <p>Mạng: {pick.mang || '—'}</p>
            <p>Địa chỉ: {pick.direction === 'WITHDRAW' ? <Reveal masked={pick.diaChi} source="withdraw" id={pick.id} field="diaChi" copy={!!rutMo} copyDisabled={khoaChep} /> : pick.diaChi || '—'}</p>
            <p>Số USDT: {pick.usdtSo || '—'}{pick.gia ? ` · tỷ giá ${pick.gia}` : ''}
              {rutMo && pick.usdtSo ? <CopyBtn get={() => pick.usdtSo ?? null} disabled={khoaChep} /> : null}</p>
          </>
        ) : game ? (
          <>
            {rutMo ? (
              // rút 1 bước: what the bank app needs, each with its copy button (the account number through the audited reveal)
              <div className="copy-rows" aria-label="Thông tin chuyển khoản">
                <p>Ngân hàng: <strong>{pick?.nganHang || '—'}</strong>{pick?.nganHang ? <CopyBtn get={() => pick.nganHang ?? null} disabled={khoaChep} /> : null}</p>
                <p>Số tài khoản: <Reveal masked={pick?.soTk} source="withdraw" id={pick!.id} copy copyDisabled={khoaChep} /></p>
                <p>Tên chủ tài khoản: <strong>{pick?.tenTk || '—'}</strong>{pick?.tenTk ? <CopyBtn get={() => pick.tenTk ?? null} disabled={khoaChep} /> : null}</p>
                <p>Số tiền: <strong><Money v={pick?.amount} /></strong><CopyBtn get={() => pick?.amount ?? null} disabled={khoaChep} /></p>
              </div>
            ) : (
              <>
                <p>Ngân hàng: {pick?.nganHang || '—'}</p>
                <p>{pick?.direction === 'DEPOSIT' ? 'Tài khoản nhận' : 'Chuyển tới'}: {pick?.direction === 'WITHDRAW' ? <Reveal masked={pick.soTk} source="withdraw" id={pick.id} /> : pick?.soTk || '—'} · {pick?.tenTk || '—'}</p>
              </>
            )}
            {pick?.direction === 'DEPOSIT' ? (
              <>
                <p>Mã nội dung: <strong>{pick.noiDung || '—'}</strong></p>
                <p>Người gửi khai: {pick.nguoiGui || '—'}{pick.tenDangKy ? ' · tên tài khoản rút đã đăng ký: ' + pick.tenDangKy : ''}
                  {pick.tenLech ? <Tag type="red" size="sm">khác tên</Tag> : null}</p>
              </>
            ) : null}
            {pick?.bankTx ? <p>Giao dịch ngân hàng: {pick.bankTx.maGd
              ? <Link to={'/wallet/bank-tx?status=&ref=' + encodeURIComponent(pick.bankTx.maGd)}>{pick.bankTx.maGd}</Link>
              : 'duyệt tay không mã'} ({BANK_TX_SOURCE[pick.bankTx.nguon] ?? pick.bankTx.nguon})</p> : null}
            {pick?.direction === 'WITHDRAW' && pick.ruiRo ? <RiskBlock r={pick.ruiRo} /> : null}
            {pick?.lyDo || pick?.status === 'REJECTED' ? <p>Lý do: {pick.lyDo || '—'}</p> : null}
            {pick?.maChuyen ? <p>Mã giao dịch: {pick.maChuyen}</p> : null}
          </>
        ) : pick?.note ? <p>{pick.note}</p> : null}
        {hetHan && choDuyet ? <p className="muted">Lệnh đã hết hạn. Tiền về muộn thường nằm ở hàng giao dịch chưa khớp: ghép ở đó. Duyệt tay ở đây cần lý do.</p> : null}
        {pick?.amount === '0' && choDuyet && pick.kenh === 'usdt' ? (
          <>
            <TextInput id="w-usdt" labelText="Số USDT đã nhận" value={usdtNhan} onChange={(e) => setUsdtNhan(e.target.value.replace(/[^0-9.]/g, ''))} />
            <p className="muted">{pick.gia && usdtNhan && /^\d+(\.\d{1,6})?$/.test(usdtNhan)
              ? <>Cộng khoảng <Money v={String(Math.floor(Number(usdtNhan) * Number(pick.gia)))} /> (tỷ giá {pick.gia} won / USDT, máy chủ tính chính xác)</>
              : 'Tỷ giá lấy theo lúc người chơi mở lệnh.'}</p>
          </>
        ) : pick?.amount === '0' && choDuyet
          ? <TextInput id="w-amt" labelText="Số tiền đã nhận" value={soNhan} onChange={(e) => setSoNhan(e.target.value.replace(/\D/g, ''))} />
          : null}
        {game && (choTra || motBuoc || maNap)
          ? <TextInput id="w-ref" labelText={choTra || motBuoc ? 'Mã giao dịch ngân hàng (không bắt buộc; một mã chỉ dùng cho một lệnh rút)'
            : `Mã giao dịch ngân hàng (${canMaNap ? 'bắt buộc' : 'không bắt buộc'}; một mã chỉ cộng một lần)`} value={maCk} onChange={(e) => setMaCk(e.target.value)} />
          : null}
        {maNap && nghiTrung && !maCk.trim()
          ? <Checkbox id="w-trung" checked={daKiemTrung} onChange={(_e, { checked }) => setDaKiemTrung(checked)}
            labelText="Tôi đã kiểm tra sao kê: tiền của lệnh này là khoản riêng, chưa cộng cho lệnh nào khác" />
          : null}
        {/* nap: khong o nhap nao (khach 2026-10-02), tru lenh het han can ly do; rut giu o ly do khong bat buoc */}
        {game && (hetHan || ((choDuyet || hoanRut) && pick?.direction === 'WITHDRAW'))
          ? <TextInput id="w-note" labelText={hetHan ? 'Lý do duyệt tay (bắt buộc)' : 'Lý do (không bắt buộc)'} value={lyDo} onChange={(e) => setLyDo(e.target.value)} />
          : null}
        {coGiuRut ? (
          <div className="risk-block">
            {giuDoi ? <p className="muted" role="status">Danh sách giữ rút vừa thay đổi: kiểm tra các mục trên rồi ghi lại.</p> : null}
            {can('risk.review') ? (
              <>
                <TextInput id="w-go-giu" labelText="Đóng các mục giữ rút đã kiểm tra (không bắt buộc): ghi đã kiểm tra gì, vào hàng chờ rủi ro" value={goGiu} onChange={(e) => { setGoGiu(e.target.value); setGiuDoi(false); }} />
                <Button kind="tertiary" size="sm" disabled={goGiu.trim().length < 3 || release.isPending} onClick={() => release.mutate()}>Gỡ giữ rút</Button>
              </>
            ) : null}
          </div>
        ) : null}
        {game && pick?.direction === 'WITHDRAW' && can('wallet.review')
          ? <Button kind="ghost" size="sm" disabled={resetMk.isPending} onClick={() => resetMk.mutate()}>Xóa mật khẩu rút của người chơi</Button>
          : null}
        {!choDuyet && !choTra && !hoanRut && !motBuoc && (pick?.status === 'PENDING_REVIEW' || pick?.status === 'APPROVED')
          ? <p className="muted">Tài khoản của bạn không có quyền xử lý lệnh này.</p> : null}
        <InlineError error={decide.error || (pay.error && !risksFromRefusal(pay.error) ? pay.error : null) || release.error || resetMk.error || (coDanhSach ? risksQ.error : null)} />
      </Modal>
      {/* chi gan khi dang hoi: hai Modal cung gan thi hop nay dong sau go lop khoa cuon trang cua hop lenh */}
      {pick && hoi ? <Modal open size="xs" danger={hoi !== 'duyet' && hoi !== 'chi'}
        modalHeading={hoi === 'chi' ? 'Xác nhận đã chuyển xong' : hoi === 'tiep-quan' ? 'Tiếp quản lệnh' : hoi === 'duyet' ? 'Xác nhận duyệt lệnh' : hoi === 'tu-choi' ? 'Xác nhận từ chối lệnh' : 'Xác nhận hoàn tiền'}
        primaryButtonText="OK" secondaryButtonText="Quay lại"
        primaryButtonDisabled={decide.isPending || pay.isPending || ((hoi === 'duyet' || hoi === 'chi') && !!khoaChinh)}
        onRequestClose={() => setHoi(null)} onSecondarySubmit={() => setHoi(null)}
        onRequestSubmit={() => {
          if (decide.isPending || pay.isPending || Date.now() - hoiLuc.current < 500) return;
          const h = hoi;
          setHoi(null);
          if (h === 'chi') pay.mutate();
          else if (h === 'tiep-quan') void nhanLenh(true);
          else decide.mutate(h === 'duyet');
        }}>
        <p>Mã lệnh: <strong>{pick.id}</strong></p>
        <p>Người chơi: {(pick.username ?? pick.nickname ?? '') + ' #' + pick.gameUserId}</p>
        {/* lenh mo voi so tien 0: duyet cong so da go; tu choi khong cong gi nen hien so cua lenh */}
        <p>Số tiền: {pick.amount === '0' && hoi === 'duyet'
          ? (pick.kenh === 'usdt' ? usdtNhan + ' USDT' : <Money v={soNhan} />) : <Money v={pick.amount} />}</p>
        {hoi === 'tiep-quan' ? <p className="num--neg">{claim?.by ?? 'Người đang xử lý'} có thể đã chuyển khoản: kiểm tra sao kê ngân hàng trước khi chuyển hoặc từ chối.</p> : null}
        <p>Bấm OK để {hoi === 'chi' ? 'xác nhận đã chuyển xong (lệnh thành Đã chi)' : hoi === 'tiep-quan' ? 'nhận lệnh này về bạn' : hoi === 'duyet' ? primary.toLowerCase() : hoi === 'tu-choi' ? 'từ chối lệnh' : 'hoàn tiền cho người chơi'}.</p>
      </Modal> : null}
    </div>
  );
}

function RiskBlock({ r }: { r: WithdrawRisk }) {
  return (
    <div className="risk-block">
      <p>Rủi ro: <RiskScore r={r} />{r.xacNhan ? <span className="muted"> · đã xác nhận</span> : null}</p>
      {r.lyDo.length ? (
        <ul className="risk-reasons">
          {r.lyDo.map((x) => <li key={x.ma}><strong>+{x.diem}</strong> {x.chu}</li>)}
        </ul>
      ) : <p className="muted">Không có dấu hiệu rủi ro.</p>}
      {/* IP chi founder nhan (owner 2026-10-23, rut-an-ip-rui-ro): vai tro khac chi thay thiet bi */}
      {r.may.ip || r.may.fg ? <p className="muted">{r.may.ip ? `IP ${r.may.ip} · thiết bị ` : 'Thiết bị '}{r.may.fg ? r.may.fg.slice(0, 16) : '—'}</p> : null}
    </div>
  );
}
